import React, { useEffect, useState, useCallback } from "react";
import {
  LayoutGrid, Radio, GitBranch, Settings as SettingsIcon, Menu, X, RefreshCw, GitPullRequest,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import Overview from "../components/dashboard/Overview";
import FrictionFeed from "../components/dashboard/FrictionFeed";
import Repositories from "../components/dashboard/Repositories";
import Settings from "../components/dashboard/Settings";
import Deliveries from "../components/dashboard/Deliveries";
import DiagnosticDrawer from "../components/dashboard/DiagnosticDrawer";

const NAV = [
  { id: "overview", label: "Adoption Risk", icon: LayoutGrid },
  { id: "feed", label: "Friction Feed", icon: Radio },
  { id: "repos", label: "Repositories", icon: GitBranch },
  { id: "deliveries", label: "Deliveries", icon: GitPullRequest },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const TITLES = {
  overview: "Adoption Risk Dashboard",
  feed: "Friction Feed",
  repos: "Watched Repositories",
  deliveries: "Zero-Handoff Deliveries",
  settings: "Settings",
};

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const [view, setView] = useState("overview");
  const [repos, setRepos] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [signals, setSignals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const [r, m, s] = await Promise.all([
      api.get("/repos"),
      api.get("/dashboard/metrics"),
      api.get("/friction"),
    ]);
    setRepos(r.data);
    setMetrics(m.data);
    setSignals(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pollBilling = async (sessionId, attempts) => {
    if (attempts >= 6) { toast.error("Payment status pending — check back shortly."); return; }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      if (res.data.subscription_active || res.data.payment_status === "paid") {
        toast.success("Subscription active — your plan is live!");
        checkAuth();
        return;
      }
      if (res.data.status === "expired") { toast.error("Payment session expired."); return; }
    } catch (e) { /* keep polling */ }
    setTimeout(() => pollBilling(sessionId, attempts + 1), 2000);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing");
    const sessionId = params.get("session_id");
    if (billing === "cancelled") {
      toast.info("Checkout cancelled");
      window.history.replaceState({}, "", "/dashboard");
    } else if (billing === "success" && sessionId) {
      setView("settings");
      pollBilling(sessionId, 0);
      window.history.replaceState({}, "", "/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-60 bg-foreground text-background flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-background/15">
          <Logo className="text-background" />
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden"><X size={18} /></button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setView(n.id); setSidebarOpen(false); }} data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 font-mono text-sm border-2 transition-colors ${view === n.id ? "bg-primary text-white border-primary" : "border-transparent hover:bg-background/10"}`}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-background/15 font-mono text-[11px] text-background/50">
          <div className="flex items-center gap-2"><span className="w-2 h-2 bg-accent live-dot" /> telemetry online</div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-foreground/40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b-2 border-foreground bg-background flex items-center justify-between px-5 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden" data-testid="sidebar-toggle"><Menu size={20} /></button>
            <h1 className="font-display font-extrabold tracking-tight text-lg sm:text-xl">{TITLES[view]}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refresh} data-testid="dashboard-refresh-btn" className="btn btn-outline btn-sm">
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <div className="hidden sm:flex items-center gap-2 font-mono text-sm">
              <span className="text-muted-foreground">{user?.name}</span>
              {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 border border-foreground" referrerPolicy="no-referrer" />}
            </div>
          </div>
        </header>

        <main className="flex-1 p-5">
          {view === "overview" && <Overview metrics={metrics} onGoRepos={() => setView("repos")} />}
          {view === "feed" && <FrictionFeed signals={signals} onSelect={setSelected} />}
          {view === "repos" && <Repositories repos={repos} onChange={load} onGoFeed={() => setView("feed")} />}
          {view === "deliveries" && <Deliveries onOpenSignal={setSelected} />}
          {view === "settings" && <Settings user={user} onLogout={logout} />}
        </main>
      </div>

      {selected && <DiagnosticDrawer signalId={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}
